import { TippyProps } from '@tippyjs/react';
import React, { useMemo, useRef, useState } from 'react';
import { LuChevronDown } from 'react-icons/lu';
import type { Instance } from 'tippy.js';
import Icon from '../ReactIcon';
import { Tooltip } from '../Tooltip';
import { DropdownContent } from './DropdownContent';
import { DropdownWrapper } from './styles';
import { motion } from 'framer-motion';

/** Caracteres extra no min-width em `ch` (ícone chevron, padding). */
const MIN_WIDTH_CH_BUFFER = 2;

type DropdownProps = {
    title: string;
    currentSelected?: string;
    allSize?: boolean;
    setCurrentSelected: (currentSelected: string) => void;
    elements: {
        id: string;
        value: string;
    }[];
    dropdownContentClassname?: string;
    maxWidth?: number;
    injectedTippyProps?: TippyProps;
    wrapperClassName?: string;
};

export const Dropdown: React.FC<DropdownProps> = ({
    title,
    elements,
    currentSelected,
    allSize,
    setCurrentSelected,
    dropdownContentClassname,
    wrapperClassName,
    injectedTippyProps,
}) => {
    const childRef = useRef<HTMLButtonElement>(null);
    const tooltipRef = useRef<Instance>();
    const [isOpen, setIsOpen] = useState(false);
    const minWidthChRunningMax = useRef(0);

    const elementsSortedByAlphabetical = useMemo(() => {
        return [...elements].sort((a, b) =>
            a.value.localeCompare(b.value, 'pt-br')
        );
    }, [elements]);

    const longestValue = useMemo(() => {
        const maxFromElements =
            elements.length > 0
                ? Math.max(...elements.map(e => e.value.length))
                : 0;
        const displayLabel = currentSelected || title;
        const computedThisFrame = Math.max(
            maxFromElements,
            title.length,
            displayLabel.length,
            1
        );
        minWidthChRunningMax.current = Math.max(
            minWidthChRunningMax.current,
            computedThisFrame
        );
        return minWidthChRunningMax.current + MIN_WIDTH_CH_BUFFER;
    }, [elements, title, currentSelected]);

    const onShow: TippyProps['onShow'] = instance => {
        setIsOpen(true);
        const childWidth = childRef.current ? childRef.current.offsetWidth : 0;
        instance.popper.style.width = `${childWidth}px`;
    };

    return (
        <Tooltip
            arrow={false}
            placement="bottom"
            trigger="click"
            interactive={true}
            hideOnClick={'toggle'}
            offset={[0, 2]}
            onShow={onShow}
            onClickOutside={instance => instance.hide()}
            onHide={() => setIsOpen(false)}
            content={
                <DropdownContent
                    elements={elementsSortedByAlphabetical}
                    longestValue={longestValue}
                    setCurrentSelected={val => {
                        tooltipRef.current?.hide();
                        setCurrentSelected(val);
                    }}
                    title={title}
                    className={dropdownContentClassname}
                />
            }
            maxWidth={9999}
            onMount={instance => (tooltipRef.current = instance)}
            {...injectedTippyProps}
        >
            <DropdownWrapper
                longestValue={longestValue}
                aria-selected={!!allSize}
                className={
                    (wrapperClassName ?? ' ') +
                    (isOpen ? ' dropdown open' : ' dropdown')
                }
                ref={childRef}
            >
                {currentSelected || title}
                <motion.div
                    animate={{ rotateZ: isOpen ? 180 : 0, display: 'flex' }}
                >
                    <Icon
                        Icon={LuChevronDown}
                        size={'1.25rem'}
                        color="#71717A"
                    />
                </motion.div>
            </DropdownWrapper>
        </Tooltip>
    );
};
